import React, { useEffect, useState } from 'react';
import { X, Lock, Check, Sparkles, MapPin, ShoppingBag, AlertTriangle } from 'lucide-react';
interface ExitIntentUpsellProps {
  isOpen: boolean;
  onClose: () => void;
  onAccept: () => void;
  onDecline: () => void;
}
// Argentine names + provinces for the live social proof ticker
const RECENT_BUYERS = [{
  name: 'Sofía M.',
  province: 'Mendoza'
}, {
  name: 'Camila R.',
  province: 'Córdoba'
}, {
  name: 'Martina G.',
  province: 'Buenos Aires'
}, {
  name: 'Lucía F.',
  province: 'Santa Fe'
}, {
  name: 'Valentina P.',
  province: 'Tucumán'
}, {
  name: 'Florencia D.',
  province: 'Salta'
}, {
  name: 'Agustina B.',
  province: 'Neuquén'
}, {
  name: 'Brenda L.',
  province: 'Entre Ríos'
}, {
  name: 'Daniela S.',
  province: 'San Juan'
}, {
  name: 'Julieta V.',
  province: 'Misiones'
}];
const formatPrice = (n: number) => n.toLocaleString('es-AR');
// Math (locked):
// Main: 17.999 · Bump: 9.990 · True total: 27.989
// 30% off bumps: 9.990 → 6.993 · Final bundle: 17.999 + 6.993 = 24.992
const MAIN_PRICE = 17999;
const BUMP_PRICE = 9990;
const TRUE_TOTAL = MAIN_PRICE + BUMP_PRICE; // 27.989
const DISCOUNTED_BUMP = Math.round(BUMP_PRICE * 0.7); // 6.993
const FINAL_BUNDLE = MAIN_PRICE + DISCOUNTED_BUMP; // 24.992
const SAVINGS = TRUE_TOTAL - FINAL_BUNDLE; // 2.997
export function ExitIntentUpsell({
  isOpen,
  onClose,
  onAccept,
  onDecline
}: ExitIntentUpsellProps) {
  const [buyerIdx, setBuyerIdx] = useState(0);
  // Rotate the "última compra" ticker every 3.5s
  useEffect(() => {
    if (!isOpen) return;
    const interval = setInterval(() => {
      setBuyerIdx(prev => (prev + 1) % RECENT_BUYERS.length);
    }, 3500);
    return () => clearInterval(interval);
  }, [isOpen]);
  // Lock body scroll while open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);
  // ESC to close
  useEffect(() => {
    if (!isOpen) return;
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [isOpen, onClose]);
  if (!isOpen) return null;
  const buyer = RECENT_BUYERS[buyerIdx];
  return <div role="dialog" aria-modal="true" aria-labelledby="upsell-title" className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6 animate-fade-in-overlay" data-id="element-62">
      {/* Overlay */}
      <div className="absolute inset-0 bg-black/75 backdrop-blur-sm" onClick={onClose} aria-hidden="true" data-id="element-63" />

      {/* Popup */}
      <div className="relative w-full max-w-lg bg-white rounded-3xl shadow-2xl overflow-hidden animate-fade-in-up max-h-[95vh] overflow-y-auto" data-id="element-64">
        {/* Close */}
        <button onClick={onClose} aria-label="Cerrar" className="absolute top-3 right-3 z-20 p-2 rounded-full bg-white/80 hover:bg-brand-100 text-brand-700 transition-colors" data-id="element-65">
          <X className="w-5 h-5" data-id="element-66" />
        </button>

        {/* HEADER BANNER */}
        <div className="bg-sage-900 text-sand-50 px-6 pt-8 pb-7 text-center relative overflow-hidden" data-id="element-67">
          <div className="absolute -top-10 -right-10 w-40 h-40 bg-gold-500/20 rounded-full blur-3xl pointer-events-none" data-id="element-68" />
          <div className="absolute -bottom-12 -left-10 w-44 h-44 bg-terracotta-500/20 rounded-full blur-3xl pointer-events-none" data-id="element-69" />

          <div className="relative z-10" data-id="element-70">
            <div className="inline-flex items-center gap-2 bg-terracotta-600 text-white px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-widest mb-4 shadow-sm border border-terracotta-500" data-id="element-71">
              <AlertTriangle className="w-3.5 h-3.5" data-id="element-72" />
              ¡Esperá! Última oportunidad
            </div>
            <h2 id="upsell-title" className="font-heading text-2xl sm:text-3xl font-semibold leading-tight mb-2 text-balance text-embossed" data-id="element-73">
              🛑 ¡Frená un segundo!
              <br data-id="element-74" />
              Estás a punto de perderte algo enorme
            </h2>
            <p className="text-sage-200 text-sm sm:text-base text-balance font-light" data-id="element-75">
              Antes de seguir, mirá esta oferta exclusiva que solo verás{' '}
              <span className="text-gold-400 font-medium italic" data-id="element-76">una vez</span>
              ...
            </p>
          </div>
        </div>

        {/* BODY */}
        <div className="px-6 pt-6 pb-6 bg-white" data-id="element-77">
          {/* Flashing discount badge */}
          <div className="flex justify-center -mt-12 mb-5 relative z-10" data-id="element-78">
            <div className="animate-flash-blink text-white font-bold px-6 py-3 rounded-2xl text-2xl sm:text-3xl tracking-widest shadow-premium border-4 border-white" data-id="element-79">
              30% OFF
            </div>
          </div>

          <p className="text-concrete-900 text-center text-base sm:text-lg font-medium mb-2 text-balance" data-id="element-80">
            Sumá los{' '}
            <span className="text-terracotta-500" data-id="element-81">
              Moldes de Silicona Caseros
            </span>{' '}
            con 30% de descuento y empezá a crear piezas únicas en menos de 24
            horas.
          </p>
          <p className="text-concrete-500 text-center text-sm mb-6 text-balance font-light" data-id="element-82">
            Este combo te da todo lo que necesitás para transformar tu pasión en
            un ingreso real desde casa, sin experiencia previa.
          </p>

          {/* Bundle visual */}
          <div className="mb-5 rounded-2xl overflow-hidden bg-sand-50 border border-sand-100" data-id="element-83">
            <img src="https://cdn.shopify.com/s/files/1/0999/7487/3381/files/bundle.png?v=1779992163" alt="Combo completo Arte con Cemento + Moldes de Silicona Caseros" className="w-full h-40 sm:h-48 object-contain p-3 mix-blend-multiply" data-id="element-84" />
          </div>

          {/* WHAT'S INCLUDED */}
          <div className="bg-sand-50 rounded-2xl p-5 mb-5 border border-sand-100" data-id="element-85">
            <p className="text-[10px] font-bold text-concrete-500 uppercase tracking-widest mb-3 flex items-center gap-2" data-id="element-86">
              <ShoppingBag className="w-4 h-4" data-id="element-87" />
              El combo completo incluye:
            </p>
            <ul className="space-y-3" data-id="element-88">
              <li className="flex items-start gap-3 text-sm" data-id="element-89">
                <Check className="w-4 h-4 text-sage-600 flex-shrink-0 mt-0.5" strokeWidth={2.5} data-id="element-90" />
                <span className="text-concrete-700 font-light" data-id="element-91">
                  <strong className="font-medium text-concrete-900" data-id="element-92">
                    Arte con Cemento
                  </strong>{' '}
                  — Programa completo paso a paso
                </span>
              </li>
              <li className="flex items-start gap-3 text-sm" data-id="element-93">
                <Check className="w-4 h-4 text-sage-600 flex-shrink-0 mt-0.5" strokeWidth={2.5} data-id="element-94" />
                <span className="text-concrete-700 font-light" data-id="element-95">
                  <strong className="font-medium text-concrete-900" data-id="element-96">
                    Bono #1:
                  </strong>{' '}
                  Guía de Pigmentos y Colores
                </span>
              </li>
              <li className="flex items-start gap-3 text-sm" data-id="element-97">
                <Check className="w-4 h-4 text-sage-600 flex-shrink-0 mt-0.5" strokeWidth={2.5} data-id="element-98" />
                <span className="text-concrete-700 font-light" data-id="element-99">
                  <strong className="font-medium text-concrete-900" data-id="element-100">
                    Bono #2:
                  </strong>{' '}
                  Manual para vender en Instagram
                </span>
              </li>
              <li className="flex items-start gap-3 text-sm bg-gold-50 -mx-2 px-2 py-2 rounded-lg border border-gold-200/50" data-id="element-101">
                <Sparkles className="w-4 h-4 text-gold-600 flex-shrink-0 mt-0.5" data-id="element-102" />
                <span className="text-concrete-800 font-light" data-id="element-103">
                  <strong className="font-medium text-concrete-900" data-id="element-104">
                    + NUEVO:
                  </strong>{' '}
                  Moldes de Silicona Caseros
                </span>
              </li>
            </ul>
          </div>

          {/* PRICING BLOCK */}
          <div className="bg-sage-900 text-white rounded-2xl p-6 mb-6 text-center relative overflow-hidden shadow-inner" data-id="element-105">
            <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1618220179428-22790b46a0eb?q=80&w=2000&auto=format&fit=crop')] bg-cover bg-center opacity-10 mix-blend-overlay" data-id="element-106"></div>

            <div className="relative z-10" data-id="element-107">
              <div className="flex items-center justify-center gap-2 text-gold-400 text-[10px] font-bold uppercase tracking-widest mb-3" data-id="element-108">
                <Lock className="w-3.5 h-3.5" data-id="element-109" />
                Precio exclusivo solo en esta pantalla
              </div>

              <p className="text-sage-200 text-sm mb-1 font-light" data-id="element-110">
                Antes:{' '}
                <span className="line-through font-medium" data-id="element-111">
                  ${formatPrice(TRUE_TOTAL)} ARS
                </span>
              </p>

              <div className="flex items-baseline justify-center gap-1 mb-2" data-id="element-112">
                <span className="text-terracotta-400 text-5xl sm:text-6xl font-heading font-semibold" data-id="element-113">
                  ${formatPrice(FINAL_BUNDLE)}
                </span>
                <span className="text-xl font-medium text-sage-100" data-id="element-114">ARS</span>
              </div>

              <p className="text-gold-400 text-sm font-medium tracking-wide" data-id="element-115">
                ¡Ahorrás ${formatPrice(SAVINGS)} ARS hoy!
              </p>
            </div>
          </div>

          {/* CTAs */}
          <div className="space-y-3" data-id="element-116">
            <button onClick={onAccept} className="
                w-full bg-terracotta-gradient text-white font-medium uppercase
                rounded-full py-4 px-4 text-sm sm:text-base tracking-widest
                transition-all duration-300 transform hover:-translate-y-1 active:scale-95
                animate-glow-pulse text-balance leading-tight shadow-premium
              " data-id="element-117">
              ✅ ¡SÍ! Quiero el combo completo
            </button>

            <button onClick={onDecline} className="
                w-full text-concrete-400 hover:text-concrete-600
                font-light text-xs tracking-wide py-2 transition-colors uppercase
              " data-id="element-118">
              No gracias, continuar sin los extras →
            </button>
          </div>

          {/* Live social proof — Argentine buyers */}
          <div key={buyerIdx} className="mt-6 flex items-center justify-center gap-2 text-[10px] text-concrete-400 uppercase tracking-widest animate-fade-in-overlay" data-id="element-119">
            <span className="w-1.5 h-1.5 rounded-full bg-sage-500 animate-pulse" data-id="element-120" />
            <MapPin className="w-3 h-3 text-gold-500" data-id="element-121" />
            <span data-id="element-122">
              <strong className="text-concrete-600 font-medium" data-id="element-123">
                {buyer.name}
              </strong>{' '}
              de{' '}
              <strong className="text-concrete-600 font-medium" data-id="element-124">
                {buyer.province}
              </strong>{' '}
              acaba de aprovechar esta oferta
            </span>
          </div>
        </div>
      </div>
    </div>;
}